import {
	createContext,
	isValidElement,
	type ReactElement,
	type ReactNode,
	useContext,
	useEffect,
	useId,
	useMemo,
	useState,
} from "react";
import type { FlexJustify } from "../../styling";
import { type FlexAlign, flexAlign, flexJustify, type Gap, tw } from "../../styling";
import type { Speed } from "../expandable";
import { Expandable } from "../expandable";
import { Stack } from "../stack";
import { ToggleChevron } from "../toggle-chevron";
import { Heading } from "../typography";

type Props = {
	children: ReactNode;
	expandedDefault?: boolean;
	callback?: (openState: boolean) => void;
	controlledOpen?: boolean;
	label?: string;
	gap?: Gap;
};

export const AccordionContext = createContext<{
	isOpen: boolean;
	handleIsOpen: () => void;
	label?: string;
	headingId?: string;
	contentId?: string;
}>({ isOpen: false, handleIsOpen: () => {} });

const AccordionHeading = ({
	children,
	align = "center",
	justify,
	fullWidth = true,
	noChevron = false,
}: {
	children: ReactElement | string;
	align?: FlexAlign;
	justify?: FlexJustify;
	fullWidth?: boolean;
	noChevron?: boolean;
}) => {
	const { handleIsOpen, isOpen, headingId, contentId } = useContext(AccordionContext);
	return (
		<button
			id={headingId}
			type="button"
			onClick={(event) => {
				event.stopPropagation();
				handleIsOpen();
			}}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === " ") {
					event.stopPropagation();
				}
			}}
			aria-expanded={isOpen}
			aria-controls={contentId}
			className={`
				focus-visible:outline-interactive-primary
				w-full 
				cursor-pointer
  				rounded-md
				text-left
  				focus-visible:outline-2
				focus-visible:outline-offset-8
				focus-visible:outline-solid`}
		>
			<div
				className={tw(
					"flex w-full gap-4",
					fullWidth && "justify-between",
					justify && flexJustify[justify],
					align && flexAlign[align],
				)}
			>
				<div className={tw(fullWidth && "flex-1")}>
					{isValidElement(children) ? children : <Heading.H3>{children}</Heading.H3>}
				</div>
				{!noChevron && <ToggleChevron open={isOpen} />}
			</div>
		</button>
	);
};

const AccordionContent = ({ children, speed }: { children: ReactNode; speed?: Speed }) => {
	const { isOpen, headingId, contentId } = useContext(AccordionContext);
	return (
		<Expandable isExpanded={isOpen} headingId={headingId} contentId={contentId} speed={speed}>
			{children}
		</Expandable>
	);
};

export const Accordion = ({
	children,
	expandedDefault = false,
	callback,
	controlledOpen,
	label,
	gap = 0,
}: Props) => {
	const [isOpen, setIsOpen] = useState(expandedDefault);
	const headingId = useId();
	const contentId = useId();
	const handleIsOpen = () => {
		setIsOpen(!isOpen);
		callback?.(!isOpen);
	};
	const state = useMemo(
		() => ({
			isOpen: controlledOpen || isOpen,
			handleIsOpen,
			label,
			headingId,
			contentId,
		}),
		[isOpen, handleIsOpen, setIsOpen],
	);

	useEffect(() => {
		if (controlledOpen !== undefined) {
			setIsOpen(controlledOpen);
		}
	}, [controlledOpen]);

	return (
		<AccordionContext.Provider value={state}>
			<Stack gap={gap}>{children}</Stack>
		</AccordionContext.Provider>
	);
};

Accordion.Heading = AccordionHeading;
Accordion.Content = AccordionContent;
